/**
 * Shared verification email utilities.
 * Handles cooldown tracking (localStorage) and the resend API call.
 * Used by both the auto-send flow (handleStreamError) and ResendVerificationButton.
 */

import { apiClient } from "./client";

export const COOLDOWN_SECONDS = 90;
const STORAGE_KEY_PREFIX = "resend_verification_cooldown_";

function storageKey(email: string) {
	return `${STORAGE_KEY_PREFIX}${email.trim().toLowerCase()}`;
}

const isBrowser = typeof window !== "undefined";

export function isOnCooldown(email: string): boolean {
	if (!isBrowser) return false;
	const expiry = localStorage.getItem(storageKey(email));
	if (!expiry) return false;
	if (Number.parseInt(expiry, 10) > Date.now()) return true;
	localStorage.removeItem(storageKey(email));
	return false;
}

export function getCooldownRemaining(email: string): number {
	if (!isBrowser) return 0;
	const expiry = localStorage.getItem(storageKey(email));
	if (!expiry) return 0;
	const remaining = Math.ceil((Number.parseInt(expiry, 10) - Date.now()) / 1000);
	if (remaining <= 0) {
		localStorage.removeItem(storageKey(email));
		return 0;
	}
	return remaining;
}

function setCooldown(email: string) {
	localStorage.setItem(storageKey(email), (Date.now() + COOLDOWN_SECONDS * 1000).toString());
}

export async function sendVerificationEmail(
	email: string
): Promise<{ success: boolean; error?: string }> {
	const normalizedEmail = email.trim();
	try {
		const { error } = await apiClient.POST("/api/v1/auth/resend-verification", {
			body: { email: normalizedEmail },
		});
		if (error) {
			return {
				success: false,
				error:
					typeof error.detail === "string" ? error.detail : "Failed to send verification email",
			};
		}
		setCooldown(email);
		return { success: true };
	} catch {
		return { success: false, error: "Failed to send verification email" };
	}
}
